import type { Lab, LabCheck } from "./labTypes";

type Json = Record<string, unknown>;
const obj = (v: unknown): Json | undefined =>
  typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Json) : undefined;
const props = (schema: unknown): Json => obj(obj(schema)?.properties) ?? {};
const typesOf = (p: unknown): string[] => {
  const t = obj(p)?.type;
  return Array.isArray(t) ? t.map(String) : typeof t === "string" ? [t] : [];
};

const TOOL_CHECKS: LabCheck[] = [
  {
    label: "Tên tool dạng snake_case, mô tả hành động (ví dụ get_order_status)",
    test: (v) => /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/.test(String(obj(v)?.name ?? "")),
    hint: "Dùng chữ thường, nối bằng dấu gạch dưới, bắt đầu bằng động từ: get_…, search_…",
  },
  {
    label: "Description chi tiết (≥ 150 ký tự): làm gì, khi nào dùng / không dùng",
    test: (v) => String(obj(v)?.description ?? "").trim().length >= 150,
    hint: "Description là \"prompt\" của tool. Nêu dữ liệu trả về, khi nào nên dùng và phân biệt với tool tương tự.",
  },
  {
    label: "input_schema có type \"object\"",
    test: (v) => obj(obj(v)?.input_schema)?.type === "object",
    hint: "input_schema là JSON Schema; gốc phải là { \"type\": \"object\", \"properties\": {…} }",
  },
  {
    label: "Có tham số order_id kiểu string",
    test: (v) => typesOf(props(obj(v)?.input_schema).order_id).includes("string"),
    hint: "Thêm properties.order_id với type \"string\".",
  },
  {
    label: "Mọi tham số đều có description (kèm định dạng/ví dụ)",
    test: (v) => {
      const ps = Object.values(props(obj(v)?.input_schema));
      return ps.length > 0 && ps.every((p) => String(obj(p)?.description ?? "").trim().length >= 10);
    },
    hint: "Mô tả ý nghĩa và định dạng, ví dụ: \"Mã đơn hàng dạng ORD-12345\".",
  },
  {
    label: "Khai báo required, và required chỉ chứa tham số có thật",
    test: (v) => {
      const s = obj(obj(v)?.input_schema);
      const req = s?.required;
      const keys = Object.keys(props(s));
      return Array.isArray(req) && req.length > 0 && req.every((r) => keys.includes(String(r)));
    },
    hint: "\"required\": [\"order_id\"]",
  },
  {
    label: "additionalProperties: false (để có thể bật strict: true)",
    test: (v) => obj(obj(v)?.input_schema)?.additionalProperties === false,
    hint: "Strict tool use yêu cầu additionalProperties: false.",
  },
];

const EXTRACT_CHECKS: LabCheck[] = [
  {
    label: "Gốc schema là object với properties",
    test: (v) => obj(v)?.type === "object" && Object.keys(props(v)).length > 0,
    hint: "{ \"type\": \"object\", \"properties\": { … } }",
  },
  {
    label: "invoice_number và total_amount là required",
    test: (v) => {
      const req = obj(v)?.required;
      return Array.isArray(req) && req.includes("invoice_number") && req.includes("total_amount");
    },
    hint: "Hai trường luôn có trên hóa đơn → đưa vào required.",
  },
  {
    label: "total_amount là number",
    test: (v) => typesOf(props(v).total_amount).includes("number"),
    hint: "Số tiền dùng type number để validate và tính toán.",
  },
  {
    label: "po_number cho phép null (thường vắng mặt → tránh bịa)",
    test: (v) => {
      const t = typesOf(props(v).po_number);
      const anyOf = obj(props(v).po_number)?.anyOf;
      const viaAnyOf = Array.isArray(anyOf) && anyOf.some((x) => obj(x)?.type === "null");
      return (t.includes("null") && t.includes("string")) || viaAnyOf;
    },
    hint: "\"type\": [\"string\", \"null\"] — và chỉ dẫn trả null khi không thấy.",
  },
  {
    label: "document_type là enum có giá trị \"other\"",
    test: (v) => {
      const e = obj(props(v).document_type)?.enum;
      return Array.isArray(e) && e.includes("other") && e.length >= 3;
    },
    hint: "Enum đóng ép nhãn sai cho tài liệu lạ — thêm \"other\".",
  },
  {
    label: "Có document_type_detail (string, nullable) để mô tả khi chọn \"other\"",
    test: (v) => {
      const t = typesOf(props(v).document_type_detail);
      return t.includes("string") && t.includes("null");
    },
    hint: "Pattern \"other + detail\" giữ cấu trúc mà vẫn trung thực.",
  },
];

const hasAny = (raw: string, words: string[]) => words.some((w) => raw.toLowerCase().includes(w));

const PROMPT_CHECKS: LabCheck[] = [
  {
    label: "Nêu đối tượng người đọc / bối cảnh",
    test: (_, raw) => hasAny(raw, ["khách hàng", "người đọc", "đối tượng", "gửi cho", "dành cho", "audience", "customer", "reader", "recipient"]),
    hint: "Ai sẽ đọc? Họ đang ở tình huống nào?",
  },
  {
    label: "Nêu mục tiêu / kết quả mong muốn",
    test: (_, raw) => hasAny(raw, ["mục tiêu", "mục đích", "để ", "nhằm", "goal", "purpose", "objective", "so that", "in order to"]),
    hint: "Email này cần đạt được điều gì?",
  },
  {
    label: "Chỉ định giọng điệu",
    test: (_, raw) => hasAny(raw, ["giọng", "lịch sự", "thân thiện", "trang trọng", "đồng cảm", "tone", "polite", "friendly", "formal", "empathetic", "sincere"]),
    hint: "Ví dụ: đồng cảm, chuyên nghiệp, không đổ lỗi.",
  },
  {
    label: "Ràng buộc định dạng / độ dài",
    test: (_, raw) => /\d+\s*(từ|câu|đoạn|dòng|words?|sentences?|paragraphs?|lines?)/i.test(raw) || hasAny(raw, ["định dạng", "tối đa", "không quá", "gạch đầu dòng", "format", "at most", "no more than", "bullet"]),
    hint: "Ví dụ: tối đa 150 từ, 3 đoạn, có tiêu đề.",
  },
  {
    label: "Tách dữ liệu đầu vào bằng thẻ XML",
    test: (_, raw) => /<([a-z_]+)>[\s\S]*<\/\1>/i.test(raw),
    hint: "Bọc thông tin đơn hàng trong <order_info>…</order_info>.",
  },
  {
    label: "Prompt đủ chi tiết (≥ 250 ký tự)",
    test: (_, raw) => raw.trim().length >= 250,
    hint: "Prompt tốt thường dài hơn bạn nghĩ — ngữ cảnh là chìa khóa.",
  },
];

export const LABS: Lab[] = [
  // ───────────── Beginner ─────────────
  {
    id: "lab-prompt-rewrite", exam: "ccao-f", type: "build", format: "text", minutes: 10,
    title: "Viết lại một prompt tệ", skill: "Prompting",
    intro: "Prompt gốc: \"viết email xin lỗi khách vì giao hàng trễ\". Kết quả nhận được chung chung, sai giọng điệu, quá dài.",
    task: "Viết lại prompt hoàn chỉnh. Thông tin có sẵn: khách Nguyễn An, đơn ORD-88123, trễ 3 ngày do kho quá tải, công ty tặng mã giảm 15% (GIAM15).",
    starter: "",
    checks: PROMPT_CHECKS,
    solution: `Bạn là nhân viên chăm sóc khách hàng của cửa hàng.
Hãy viết email xin lỗi gửi cho khách hàng về việc giao hàng trễ.

<order_info>
Khách hàng: Nguyễn An
Mã đơn: ORD-88123
Trễ: 3 ngày, do kho quá tải dịp khuyến mãi
Bồi thường: mã giảm 15% (GIAM15) cho đơn tiếp theo
</order_info>

Mục tiêu: khách cảm thấy được tôn trọng và tiếp tục mua hàng.
Giọng điệu: chân thành, đồng cảm, không đổ lỗi, không sáo rỗng.
Định dạng: tiêu đề email + tối đa 120 từ, 3 đoạn ngắn; kết thúc bằng mã giảm giá và cách liên hệ hỗ trợ.`,
    takeaway: "Ngữ cảnh + mục tiêu + giọng điệu + định dạng + dữ liệu tách bạch = prompt tốt. Claude không biết điều bạn không nói.",
  },
  {
    id: "lab-choose-model", exam: "ccao-f", type: "classify", minutes: 5,
    title: "Chọn đúng model cho từng việc", skill: "Chọn model",
    intro: "Mỗi tầng model có điểm mạnh riêng về năng lực, tốc độ và chi phí.",
    task: "Gán mỗi tác vụ vào tầng model phù hợp NHẤT.",
    buckets: ["Haiku — nhanh, rẻ", "Sonnet — cân bằng", "Opus — suy luận sâu"],
    items: [
      { text: "Phân loại 2 triệu tin nhắn hỗ trợ thành 5 nhãn", bucket: 0, why: "Khối lượng lớn, tác vụ đơn giản → tối ưu chi phí/tốc độ." },
      { text: "Gợi ý tự động hoàn thành câu trong ô chat, cần độ trễ cực thấp", bucket: 0, why: "Độ trễ là ưu tiên số 1." },
      { text: "Agent coding hằng ngày: sửa bug, viết test trong repo", bucket: 1, why: "Cân bằng năng lực coding và tốc độ/chi phí." },
      { text: "Chatbot hỗ trợ khách hàng có dùng tool tra cứu đơn", bucket: 1, why: "Tác vụ agent phổ biến, cần năng lực tốt với chi phí hợp lý." },
      { text: "Phân tích rủi ro pháp lý xuyên suốt 40 hợp đồng phức tạp", bucket: 2, why: "Suy luận sâu, độ chính xác quan trọng hơn chi phí." },
      { text: "Lên kế hoạch kiến trúc migration hệ thống lớn nhiều bước", bucket: 2, why: "Tác vụ dài, phức tạp, cần suy luận nhiều bước." },
    ],
    takeaway: "Không có model \"tốt nhất cho mọi việc\". Bắt đầu từ yêu cầu chất lượng, rồi tối ưu chi phí và độ trễ — và đo bằng eval.",
  },
  // ───────────── Developer ─────────────
  {
    id: "lab-tool-loop", exam: "ccdv-f", type: "order", minutes: 5,
    title: "Vòng đời một lần gọi tool", skill: "Tool use",
    intro: "Ứng dụng của bạn khai báo tool get_weather. Người dùng hỏi: \"Hà Nội hôm nay mưa không?\"",
    task: "Sắp xếp các bước theo đúng thứ tự xảy ra.",
    steps: [
      "App gửi request gồm messages + định nghĩa tools tới Messages API",
      "Claude trả về khối tool_use (name, input) với stop_reason = \"tool_use\"",
      "App thực thi get_weather({city: \"Hà Nội\"}) trong code của mình",
      "App gửi tin nhắn user chứa tool_result với tool_use_id tương ứng",
      "Claude dùng kết quả để trả lời, stop_reason = \"end_turn\"",
    ],
    takeaway: "Claude không tự chạy client tool — ứng dụng của bạn thực thi và trả kết quả. Đây là nền tảng của mọi vòng lặp agent.",
  },
  {
    id: "lab-tool-def", exam: "ccdv-f", type: "build", format: "json", minutes: 12,
    title: "Thiết kế tool definition chuẩn production", skill: "Tool design",
    intro: "Tool hiện tại (trong ô soạn thảo bên dưới) có tên mơ hồ, mô tả 2 chữ, tham số không kiểu và không mô tả — Claude gọi sai thường xuyên.",
    task: "Viết lại định nghĩa tool tra cứu trạng thái đơn hàng theo mã đơn, đạt mọi tiêu chí bên dưới.",
    starter: `{
  "name": "order",
  "description": "lấy order",
  "input_schema": {
    "type": "object",
    "properties": {
      "id": {}
    }
  }
}`,
    checks: TOOL_CHECKS,
    solution: `{
  "name": "get_order_status",
  "description": "Tra cứu trạng thái hiện tại của MỘT đơn hàng theo mã đơn: trạng thái (pending/shipped/delivered/cancelled), mã vận đơn và ngày dự kiến giao. Dùng khi khách hỏi đơn đang ở đâu hoặc khi nào tới. KHÔNG dùng để tìm đơn theo email khách — hãy dùng search_orders_by_email.",
  "strict": true,
  "input_schema": {
    "type": "object",
    "properties": {
      "order_id": {
        "type": "string",
        "description": "Mã đơn hàng dạng ORD-12345 (phân biệt hoa thường)"
      }
    },
    "required": ["order_id"],
    "additionalProperties": false
  }
}`,
    takeaway: "Tên rõ, mô tả chi tiết có ranh giới với tool khác, tham số có mô tả, schema chặt — đây là yếu tố số 1 quyết định tool use chính xác.",
  },
  {
    id: "lab-cache-bug", exam: "ccdv-f", type: "spot", minutes: 6, language: "python",
    title: "Vì sao cache không bao giờ hit?", skill: "Prompt caching",
    intro: "Dashboard cho thấy cache_creation_input_tokens cao ở MỌI request, cache_read_input_tokens = 0.",
    task: "Chọn (các) dòng gây ra lỗi.",
    lines: [
      "POLICY = open(\"policy.md\").read()          # ~30.000 token, cố định",
      "def ask(question: str):",
      "    system = [{",
      "        \"type\": \"text\",",
      "        \"text\": f\"Thời điểm: {datetime.now()}\\n\\n{POLICY}\",",
      "        \"cache_control\": {\"type\": \"ephemeral\"},",
      "    }]",
      "    return client.messages.create(",
      "        model=MODEL, max_tokens=2048, system=system,",
      "        messages=[{\"role\": \"user\", \"content\": question}],",
      "    )",
    ],
    bugLines: [4],
    fix: "Bỏ datetime.now() khỏi tiền tố được cache (hoặc chuyển nó vào tin nhắn user, sau breakpoint). Cache khớp tiền tố từng byte — một giá trị đổi mỗi request ở đầu prompt làm mọi thứ phía sau không bao giờ khớp.",
    takeaway: "Nội dung ổn định trước, nội dung biến động sau breakpoint cuối. Kiểm chứng bằng usage.cache_read_input_tokens.",
  },
  {
    id: "lab-parallel-results", exam: "ccdv-f", type: "spot", minutes: 6, language: "json",
    title: "Trả kết quả tool song song", skill: "Tool use",
    intro: "Claude vừa trả về HAI khối tool_use (toolu_A: get_weather Hà Nội, toolu_B: get_weather Đà Nẵng). Đây là phần messages app gửi tiếp theo.",
    task: "Chọn (các) dòng sai.",
    lines: [
      "{ \"role\": \"assistant\", \"content\": [ …tool_use toolu_A…, …tool_use toolu_B… ] },",
      "{ \"role\": \"user\", \"content\": [",
      "  { \"type\": \"tool_result\", \"tool_use_id\": \"toolu_A\", \"content\": \"Mưa, 24°C\" }",
      "] },",
      "{ \"role\": \"user\", \"content\": [",
      "  { \"type\": \"tool_result\", \"content\": \"Nắng, 31°C\" }",
      "] }",
    ],
    bugLines: [4, 5],
    fix: "Gộp cả hai tool_result vào MỘT tin nhắn user, và mỗi tool_result phải có tool_use_id đúng (toolu_B bị thiếu). Tách tin nhắn còn làm Claude dần ngừng gọi tool song song.",
    takeaway: "N tool_use song song → N tool_result trong một tin nhắn user, mỗi cái khớp tool_use_id.",
  },
  // ───────────── Architect Foundations ─────────────
  {
    id: "lab-config-placement", exam: "ccar-f", type: "classify", minutes: 8,
    title: "Đặt cấu hình Claude Code đúng chỗ", skill: "Claude Code config",
    intro: "Sai vị trí cấu hình là lỗi phổ biến nhất trong đề CCAR-F (ví dụ: chuẩn của team đặt ở cấp user).",
    task: "Gán mỗi yêu cầu vào cơ chế phù hợp nhất.",
    buckets: ["~/.claude/CLAUDE.md (cá nhân)", "./CLAUDE.md (project, commit)", ".claude/rules/ với paths", "Hook / permission deny"],
    items: [
      { text: "Tôi thích câu trả lời ngắn và comment bằng tiếng Việt ở mọi dự án", bucket: 0, why: "Sở thích cá nhân, áp dụng mọi dự án." },
      { text: "Lệnh chạy test & lint của repo, quy ước đặt tên của team", bucket: 1, why: "Chuẩn chung của team phải được commit để ai cũng có." },
      { text: "Quy ước chỉ áp dụng cho file trong db/migrations/**", bucket: 2, why: "Quy tắc theo phạm vi đường dẫn, chỉ nạp khi cần." },
      { text: "Quy ước viết test chỉ cho **/*.test.ts", bucket: 2, why: "Path-scoped rule giữ context gọn." },
      { text: "TUYỆT ĐỐI không được đọc file .env", bucket: 3, why: "Ràng buộc bắt buộc → cơ chế tất định, không dựa vào prompt." },
      { text: "Chặn mọi lệnh git push --force", bucket: 3, why: "Hành động nguy hiểm → PreToolUse hook hoặc deny rule." },
    ],
    takeaway: "Sở thích → user. Chuẩn team → project. Theo đường dẫn → rules. Bắt buộc → hooks/permissions (hoặc managed settings ở cấp tổ chức).",
  },
  {
    id: "lab-escalation", exam: "ccar-f", type: "classify", minutes: 6,
    title: "Escalate hay tự giải quyết?", skill: "Reliability",
    intro: "Agent hỗ trợ khách hàng có quyền hoàn tiền ≤ $500 theo chính sách 30 ngày.",
    task: "Phân loại mỗi tình huống.",
    buckets: ["Agent tự giải quyết", "Chuyển cho nhân viên"],
    items: [
      { text: "Khách bực bội vì đơn $40 giao trễ, muốn hoàn tiền (trong 30 ngày)", bucket: 0, why: "Cảm xúc tiêu cực không phải trigger escalation; yêu cầu nằm trong thẩm quyền." },
      { text: "Khách nói: \"Cho tôi nói chuyện với người thật\"", bucket: 1, why: "Yêu cầu rõ ràng của khách luôn được tôn trọng." },
      { text: "Yêu cầu hoàn $2.300", bucket: 1, why: "Vượt thẩm quyền của agent." },
      { text: "Khách muốn hoàn tiền đơn 45 ngày vì lý do sức khỏe — chính sách không đề cập", bucket: 1, why: "Khoảng trống chính sách / ngoại lệ cần con người quyết định." },
      { text: "Khách hỏi mã vận đơn của đơn hàng", bucket: 0, why: "Tra cứu thông thường." },
      { text: "Agent đã thử 3 cách nhưng không xác minh được tài khoản", bucket: 1, why: "Không tiến triển sau nhiều lần thử." },
    ],
    takeaway: "Trigger đáng tin: khách yêu cầu, vượt thẩm quyền/khoảng trống chính sách, không tiến triển. Không dùng cảm xúc hay confidence tự báo.",
  },
  {
    id: "lab-extraction-schema", exam: "ccar-f", type: "build", format: "json", minutes: 12,
    title: "Schema trích xuất hóa đơn chống hallucination", skill: "Structured output",
    intro: "Pipeline trích xuất hóa đơn đang bịa số PO khi tài liệu không có, và ép tài liệu lạ vào nhãn sai.",
    task: "Viết JSON Schema cho các trường: invoice_number, total_amount, po_number, document_type (invoice/receipt/…), document_type_detail.",
    starter: `{
  "type": "object",
  "properties": {
    "invoice_number": { "type": "string" },
    "total_amount": { "type": "string" },
    "po_number": { "type": "string" },
    "document_type": { "type": "string", "enum": ["invoice", "receipt"] }
  },
  "required": ["invoice_number", "total_amount", "po_number", "document_type"]
}`,
    checks: EXTRACT_CHECKS,
    solution: `{
  "type": "object",
  "properties": {
    "invoice_number": { "type": "string", "description": "Số hóa đơn in trên chứng từ" },
    "total_amount": { "type": "number", "description": "Tổng tiền phải thanh toán" },
    "po_number": { "type": ["string", "null"], "description": "Số PO; null nếu không có trong tài liệu" },
    "document_type": { "type": "string", "enum": ["invoice", "receipt", "credit_note", "other"] },
    "document_type_detail": { "type": ["string", "null"], "description": "Mô tả loại tài liệu khi document_type = other" }
  },
  "required": ["invoice_number", "total_amount", "po_number", "document_type", "document_type_detail"],
  "additionalProperties": false
}`,
    takeaway: "Nullable cho trường có thể vắng mặt; enum có \"other\" + detail; kiểu dữ liệu đúng để validate. Schema đảm bảo cú pháp — vẫn cần validate ngữ nghĩa.",
  },
  {
    id: "lab-ci-job", exam: "ccar-f", type: "spot", minutes: 6, language: "yaml",
    title: "Job CI review PR bị treo", skill: "Claude Code CI/CD",
    intro: "Job GitHub Actions chạy Claude Code review PR treo tới khi hết timeout 6 giờ.",
    task: "Chọn (các) dòng gây ra lỗi.",
    lines: [
      "- name: Claude review",
      "  env:",
      "    ANTHROPIC_API_KEY: ${{ secrets.ANTHROPIC_API_KEY }}",
      "  run: |",
      "    claude \"Review diff của PR này, chỉ báo bug và lỗ hổng bảo mật\"",
      "    gh pr comment $PR --body-file review.md",
    ],
    bugLines: [4],
    fix: "Thiếu -p (headless): claude -p \"…\" --output-format json --allowedTools \"Read,Grep,Glob\". Không có -p, Claude Code mở phiên tương tác và chờ input mãi mãi.",
    takeaway: "Trong CI: -p, output có cấu trúc, giới hạn tool, và phiên review độc lập với phiên sinh code.",
  },
  {
    id: "lab-subagent-brief", exam: "ccar-f", type: "build", format: "text", minutes: 10,
    title: "Viết prompt giao việc cho subagent", skill: "Multi-agent",
    intro: "Coordinator đang giao việc bằng câu: \"Tìm thêm nguồn cho luận điểm 3\" — subagent trả kết quả lạc đề vì không thấy lịch sử của coordinator.",
    task: "Viết lại prompt giao việc đầy đủ. Luận điểm 3: \"Chi phí pin lithium giảm >80% từ 2013 đến 2023\".",
    starter: "",
    checks: [
      { label: "Nêu rõ luận điểm/nhiệm vụ cụ thể (không chỉ \"luận điểm 3\")", test: (_, r) => /pin|lithium|battery/i.test(r), hint: "Subagent không biết \"luận điểm 3\" là gì — viết đầy đủ nội dung." },
      { label: "Tiêu chí nguồn (uy tín, thời gian…)", test: (_, r) => hasAny(r, ["nguồn", "uy tín", "báo cáo", "source", "peer", "reputable", "credible", "report"]), hint: "Loại nguồn nào chấp nhận được? Từ năm nào?" },
      { label: "Định dạng trả về (danh sách, trường, trích dẫn)", test: (_, r) => hasAny(r, ["trả về", "định dạng", "danh sách", "mỗi nguồn", "return", "format", "json", "list", "each source"]), hint: "Coordinator cần gì để tổng hợp? URL, ngày, trích dẫn nguyên văn…" },
      { label: "Phạm vi / giới hạn (số lượng, điều không làm)", test: (_, r) => /\d+/.test(r) && hasAny(r, ["tối đa", "không", "chỉ", "tối thiểu", "at most", "max", "only", "do not", "don't", "minimum"]), hint: "Ví dụ: 3–5 nguồn, không viết lại báo cáo." },
      { label: "Cách xử lý khi không tìm thấy / mâu thuẫn", test: (_, r) => hasAny(r, ["không tìm thấy", "mâu thuẫn", "trái ngược", "nếu không", "conflict", "not found", "nothing is found", "contradict", "disagree", "cannot find"]), hint: "Báo rõ thay vì trả rỗng hay tự chọn." },
    ],
    solution: `Nhiệm vụ: tìm bằng chứng kiểm chứng luận điểm "Chi phí pin lithium-ion giảm hơn 80% từ 2013 đến 2023" cho báo cáo về năng lượng tái tạo.

Tiêu chí nguồn: báo cáo của tổ chức uy tín (BloombergNEF, IEA), bài peer-reviewed hoặc dữ liệu chính phủ; xuất bản từ 2020 trở lại đây.

Trả về: tối đa 5 nguồn, mỗi nguồn gồm: tiêu đề, tổ chức, URL, ngày xuất bản, trích dẫn nguyên văn chứa con số, và con số $/kWh cho các năm liên quan.

Giới hạn: chỉ tìm và trích dẫn; không viết lại báo cáo.
Nếu không tìm thấy hoặc các nguồn mâu thuẫn: nêu rõ và liệt kê cả hai con số kèm nguồn, không tự chọn.`,
    takeaway: "Subagent chỉ biết những gì bạn viết trong prompt giao việc. Mục tiêu + ngữ cảnh + tiêu chí + định dạng trả về + cách báo lỗi.",
  },
  // ───────────── Architect Professional ─────────────
  {
    id: "lab-model-upgrade", exam: "ccar-p", type: "order", minutes: 5,
    title: "Quy trình nâng cấp model an toàn", skill: "Lifecycle",
    intro: "Model mới rẻ hơn và mạnh hơn vừa ra mắt. Hệ thống của bạn phục vụ 2 triệu request/ngày.",
    task: "Sắp xếp các bước theo thứ tự hợp lý.",
    steps: [
      "Đọc migration guide: thay đổi breaking của tham số/API",
      "Cập nhật code theo breaking changes trên nhánh riêng",
      "Chạy bộ eval hiện có trên model mới, so với baseline",
      "Tinh chỉnh prompt / effort dựa trên kết quả eval",
      "Canary rollout một phần nhỏ lưu lượng, theo dõi chỉ số nghiệp vụ",
      "Mở rộng dần lên 100%, giữ phương án rollback",
    ],
    takeaway: "Không bao giờ chỉ đổi model ID rồi deploy. Eval + rollout dần + rollback.",
  },
  {
    id: "lab-governance", exam: "ccar-p", type: "classify", minutes: 7,
    title: "Phân tầng kiểm soát hành động của agent", skill: "Governance",
    intro: "Agent nội bộ có nhiều tool. Bạn cần quyết định mức kiểm soát cho từng hành động theo rủi ro.",
    task: "Gán mỗi hành động vào mức kiểm soát phù hợp.",
    buckets: ["Tự động (log)", "Cần người duyệt", "Cấm hoàn toàn"],
    items: [
      { text: "Đọc wiki nội bộ để trả lời câu hỏi", bucket: 0, why: "Chỉ đọc, rủi ro thấp." },
      { text: "Tạo ticket Jira nháp", bucket: 0, why: "Dễ hoàn tác." },
      { text: "Gửi email ra ngoài công ty thay mặt nhân viên", bucket: 1, why: "Không thể thu hồi, ảnh hưởng bên ngoài." },
      { text: "Xóa bản ghi khách hàng trong CRM", bucket: 1, why: "Không thể hoàn tác." },
      { text: "Đọc file chứa khóa bí mật production", bucket: 2, why: "Không có lý do nghiệp vụ; vi phạm least privilege." },
      { text: "Thay đổi quyền IAM của chính agent", bucket: 2, why: "Tự leo thang đặc quyền." },
    ],
    takeaway: "Mức kiểm soát tỉ lệ với khả năng hoàn tác và phạm vi ảnh hưởng. Thực thi bằng chính sách/hook, không bằng prompt.",
  },
];

export const LAB_BY_ID = new Map(LABS.map((l) => [l.id, l]));
